import {
  Directive,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  Renderer2
} from '@angular/core';
import { DOCUMENT } from '@angular/common';

@Directive({
  selector: 'img'
})
export class ImageHoverPopupDirective implements OnDestroy {
  @Input('imageHoverDelay') delayMs = 3500; // default 3.5s
  @Input('imageHoverCursor') hoverCursor?: string | null = 'pointer'; // set to 'zoom-in' to restore

  private popupEl: HTMLDivElement | null = null;
  private loaderEl: HTMLDivElement | null = null;
  private shownByClick = false;
  private addedTabindex = false;
  private lastPointer: { clientX: number; clientY: number } | null = null;

  private showTimer: any = null;
  private hideTimer: any = null;

  private popupMouseEnterHandler: ((e: Event) => void) | null = null;
  private popupMouseLeaveHandler: ((e: Event) => void) | null = null;
  private popupClickHandler: ((e: MouseEvent) => void) | null = null;
  private documentClickHandler: ((e: MouseEvent) => void) | null = null;
  private documentKeydownHandler: ((e: KeyboardEvent) => void) | null = null;

  private static spinnerStyleInjected = false;

  constructor(
    private host: ElementRef<HTMLImageElement>,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document
  ) {
    const el = this.host.nativeElement;
    const tab = el.getAttribute('tabindex');
    if (tab === null) {
      this.renderer.setAttribute(el, 'tabindex', '0');
      this.addedTabindex = true;
    }

    // Only set cursor if user explicitly requests it
    if (this.hoverCursor) {
      this.renderer.setStyle(el, 'cursor', this.hoverCursor);
    }

    this.ensureSpinnerStyle();
  }

  @HostListener('mouseenter', ['$event'])
  onMouseEnter(evt: MouseEvent) {
    this.lastPointer = { clientX: evt.clientX, clientY: evt.clientY };

    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }

    // if popup already visible, nothing else to do
    if (this.popupEl || this.shownByClick) return;

    // create loader immediately
    this.createLoader();
    this.positionLoaderFromLastPointer();

    // schedule popup after delay
    if (this.showTimer) clearTimeout(this.showTimer);
    this.showTimer = setTimeout(() => {
      this.showTimer = null;
      // if user moved away, don't open
      if (!this.lastPointer) {
        this.removeLoader();
        return;
      }
      this.removeLoader();
      this.createPopup();
      this.positionPopupFromLastPointer();
    }, this.delayMs);
  }

  @HostListener('mousemove', ['$event'])
  onMouseMove(evt: MouseEvent) {
    this.lastPointer = { clientX: evt.clientX, clientY: evt.clientY };
    if (this.popupEl) {
      this.positionPopup(evt);
    } else if (this.loaderEl) {
      this.positionLoader(evt);
    }
  }

  @HostListener('mouseleave')
  onMouseLeave() {
    if (this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }

    if (this.shownByClick) return;

    if (this.popupEl) {
      if (this.hideTimer) clearTimeout(this.hideTimer);
      this.hideTimer = setTimeout(() => {
        this.hideTimer = null;
        if (!this.shownByClick) this.removePopup();
      }, 120);
    } else {
      // remove loader immediately if not yet opened
      this.removeLoader();
    }
  }

  @HostListener('click', ['$event'])
  onClick(evt: MouseEvent) {
    if (this.popupEl) {
      this.shownByClick = false;
      this.removePopup();
    } else {
      // clicking shows the popup immediately (bypass delay/loader)
      if (this.showTimer) {
        clearTimeout(this.showTimer);
        this.showTimer = null;
      }
      this.removeLoader();
      this.shownByClick = true;
      const rect = this.host.nativeElement.getBoundingClientRect();
      const fake = { clientX: rect.left + rect.width / 2, clientY: rect.top + rect.height / 2 } as unknown as MouseEvent;
      this.lastPointer = { clientX: fake.clientX, clientY: fake.clientY };
      this.createPopup();
      this.positionPopupFromLastPointer();
      evt.stopPropagation();
    }
  }

  @HostListener('focus')
  onFocus() {
    const rect = this.host.nativeElement.getBoundingClientRect();
    const fake = { clientX: rect.left + rect.width / 2, clientY: rect.top + rect.height / 2 } as unknown as MouseEvent;
    this.lastPointer = { clientX: fake.clientX, clientY: fake.clientY };

    if (this.popupEl || this.shownByClick) return;

    // show loader on focus, then popup after delay
    this.createLoader();
    this.positionLoaderFromLastPointer();

    if (this.showTimer) clearTimeout(this.showTimer);
    this.showTimer = setTimeout(() => {
      this.showTimer = null;
      if (!this.lastPointer) {
        this.removeLoader();
        return;
      }
      this.removeLoader();
      this.createPopup();
      this.positionPopupFromLastPointer();
    }, this.delayMs);
  }

  @HostListener('blur')
  onBlur() {
    if (this.shownByClick) return;
    if (this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
    this.removeLoader();
    this.removePopup();
  }

  ngOnDestroy(): void {
    this.removeLoader();
    this.removePopup();
    if (this.addedTabindex) {
      this.renderer.removeAttribute(this.host.nativeElement, 'tabindex');
    }
    if (this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }

  /** ---------- Loader logic ---------- */
  private createLoader() {
    if (this.loaderEl) return;
    const div = this.renderer.createElement('div') as HTMLDivElement;
    this.renderer.addClass(div, 'image-hover-loader');

    // loader container styles
    this.renderer.setStyle(div, 'position', 'fixed');
    this.renderer.setStyle(div, 'z-index', '2999');
    this.renderer.setStyle(div, 'pointer-events', 'none'); // don't block pointer
    this.renderer.setStyle(div, 'transition', 'opacity 120ms ease');
    this.renderer.setStyle(div, 'opacity', '1');
    this.renderer.setStyle(div, 'display', 'flex');
    this.renderer.setStyle(div, 'align-items', 'center');
    this.renderer.setStyle(div, 'justify-content', 'center');
    this.renderer.setStyle(div, 'padding', '6px');

    // spinner element (styled by injected CSS)
    const spinner = this.renderer.createElement('div');
    this.renderer.addClass(spinner, 'ihp-spinner');
    // optional small label
    const label = this.renderer.createElement('div');
    this.renderer.setStyle(label, 'font-size', '12px');
    this.renderer.setStyle(label, 'margin-left', '8px');
    this.renderer.setProperty(label, 'textContent', 'Loading…');

    this.renderer.appendChild(div, spinner);
    this.renderer.appendChild(div, label);

    this.document.body.appendChild(div);
    this.loaderEl = div;
  }

  private positionLoaderFromLastPointer() {
    if (!this.lastPointer) return;
    this.positionLoader({ clientX: this.lastPointer.clientX, clientY: this.lastPointer.clientY } as MouseEvent);
  }

  private positionLoader(evt: MouseEvent) {
    if (!this.loaderEl) return;
    const rect = this.loaderEl.getBoundingClientRect();
    const margin = 12;
    let desiredLeft = evt.clientX + margin;
    if (desiredLeft + rect.width + 8 > window.innerWidth) {
      desiredLeft = evt.clientX - rect.width - margin;
    }
    const left = Math.max(8, Math.min(desiredLeft, window.innerWidth - rect.width - 8));
    let top = evt.clientY - rect.height / 2;
    top = Math.max(8, Math.min(top, window.innerHeight - rect.height - 8));
    this.renderer.setStyle(this.loaderEl, 'left', `${left}px`);
    this.renderer.setStyle(this.loaderEl, 'top', `${top}px`);
  }

  private removeLoader() {
    if (!this.loaderEl) return;
    this.loaderEl.remove();
    this.loaderEl = null;
  }

  /** ---------- Popup logic (with outside-click, open-on-popup-click, Esc-close) ---------- */
  private createPopup() {
    if (this.popupEl) return;
    const hostImg = this.host.nativeElement;
    const src = (hostImg as HTMLImageElement).currentSrc || hostImg.src;
    if (!src) return;

    const div = this.renderer.createElement('div') as HTMLDivElement;
    this.renderer.addClass(div, 'image-hover-popup');

    this.renderer.setStyle(div, 'position', 'fixed');
    this.renderer.setStyle(div, 'z-index', '3000');
    // start with pointer-events none to avoid immediate mouseleave on host
    this.renderer.setStyle(div, 'pointer-events', 'none');
    this.renderer.setStyle(div, 'transition', 'transform 120ms ease, opacity 120ms ease');
    this.renderer.setStyle(div, 'opacity', '1');

    const img = this.renderer.createElement('img') as HTMLImageElement;
    this.renderer.setAttribute(img, 'src', src);
    const alt = hostImg.alt || '';
    this.renderer.setAttribute(img, 'alt', alt);

    this.renderer.setStyle(img, 'display', 'block');
    this.renderer.setStyle(img, 'width', 'auto');
    this.renderer.setStyle(img, 'height', 'auto');
    this.renderer.setStyle(img, 'border-radius', '6px');
    this.renderer.setStyle(img, 'box-shadow', '0 12px 40px rgba(0,0,0,0.45)');
    this.renderer.setStyle(img, 'max-width', 'calc(80vw - 16px)');
    this.renderer.setStyle(img, 'max-height', 'calc(80vh - 16px)');

    // popup click should open the image in a new window (and not close the popup).
    // We'll stop propagation so the document click handler doesn't immediately close it.
    this.popupClickHandler = (ev: MouseEvent) => {
      ev.stopPropagation();
      try {
        // open using safe options; preserve referrer policy
        window.open(src, '_blank', 'noopener,noreferrer');
      } catch (err) {
        // fallback if popup blocked — do nothing else
        console.warn('Failed to open image in new window', err);
      }
    };

    const onLoad = () => {
      try {
        const nw = img.naturalWidth || hostImg.naturalWidth || 0;
        const nh = img.naturalHeight || hostImg.naturalHeight || 0;
        if (nw > 0 && nh > 0) {
          const viewportPadding = 16;
          const maxW = Math.max(100, Math.round(window.innerWidth * 0.8) - viewportPadding);
          const maxH = Math.max(100, Math.round(window.innerHeight * 0.8) - viewportPadding);

          let width = nw;
          let height = nh;

          if (width > maxW) {
            const ratio = maxW / width;
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }
          if (height > maxH) {
            const ratio = maxH / height;
            height = Math.round(height * ratio);
            width = Math.round(width * ratio);
          }

          this.renderer.setStyle(img, 'width', `${width}px`);
          this.renderer.setStyle(img, 'height', 'auto');
        } else {
          this.renderer.setStyle(img, 'max-width', 'calc(80vw - 16px)');
          this.renderer.setStyle(img, 'max-height', 'calc(80vh - 16px)');
        }
      } finally {
        this.positionPopupFromLastPointer();

        if (this.popupEl) {
          // enable pointer events now so user can interact with the popup
          this.renderer.setStyle(this.popupEl, 'pointer-events', 'auto');

          // popup mouse handlers (hover-in/out behavior)
          this.popupMouseEnterHandler = (() => {
            if (this.hideTimer) {
              clearTimeout(this.hideTimer);
              this.hideTimer = null;
            }
          });
          this.popupMouseLeaveHandler = (() => {
            if (this.shownByClick) return;
            if (this.hideTimer) clearTimeout(this.hideTimer);
            this.hideTimer = setTimeout(() => {
              this.hideTimer = null;
              if (!this.shownByClick) this.removePopup();
            }, 120);
          });

          // add event listeners to popup
          this.popupEl!.addEventListener('mouseenter', this.popupMouseEnterHandler);
          this.popupEl!.addEventListener('mouseleave', this.popupMouseLeaveHandler);
          this.popupEl!.addEventListener('click', this.popupClickHandler!);
        }

        // Add document click handler to close popup when clicking outside
        // Keep a reference so we can remove it later.
        this.documentClickHandler = (ev: MouseEvent) => {
          // if no popup, nothing to do
          if (!this.popupEl) return;
          const target = ev.target as Node | null;
          // If click is inside popup or inside host image, ignore
          if (!target) return;
          if (this.popupEl.contains(target) || this.host.nativeElement.contains(target)) {
            return;
          }
          // otherwise close
          this.shownByClick = false;
          this.removePopup();
        };
        this.document.addEventListener('click', this.documentClickHandler);

        // Add Esc key handler to close popup
        this.documentKeydownHandler = (ev: KeyboardEvent) => {
          if (ev.key === 'Escape' || ev.key === 'Esc') {
            this.shownByClick = false;
            this.removePopup();
          }
        };
        this.document.addEventListener('keydown', this.documentKeydownHandler);
      }
    };

    img.addEventListener('load', onLoad, { once: true });

    this.renderer.appendChild(div, img);
    this.document.body.appendChild(div);
    this.popupEl = div;

    if ((hostImg as HTMLImageElement).complete && ((hostImg as HTMLImageElement).naturalWidth > 0)) {
      setTimeout(onLoad, 0);
    }
  }

  private positionPopupFromLastPointer() {
    if (!this.lastPointer) return;
    this.positionPopup({ clientX: this.lastPointer.clientX, clientY: this.lastPointer.clientY } as MouseEvent);
  }

  private positionPopup(evt: MouseEvent) {
    if (!this.popupEl) return;
    const popupRect = this.popupEl.getBoundingClientRect();
    const hostRect = this.host.nativeElement.getBoundingClientRect();
    const margin = 12;

    let desiredLeft = evt.clientX + margin;
    if (desiredLeft + popupRect.width + 8 > window.innerWidth) {
      desiredLeft = evt.clientX - popupRect.width - margin;
    }
    let left = Math.max(8, Math.min(desiredLeft, window.innerWidth - popupRect.width - 8));

    const overlapsHost = !(left + popupRect.width < hostRect.left || left > hostRect.right ||
      (evt.clientY - popupRect.height / 2) + popupRect.height < hostRect.top || (evt.clientY - popupRect.height / 2) > hostRect.bottom);

    if (overlapsHost) {
      const rightOfHost = hostRect.right + margin;
      if (rightOfHost + popupRect.width <= window.innerWidth - 8) {
        left = Math.max(8, Math.min(rightOfHost, window.innerWidth - popupRect.width - 8));
      } else {
        const leftOfHost = hostRect.left - popupRect.width - margin;
        if (leftOfHost >= 8) {
          left = leftOfHost;
        }
      }
    }

    let top = evt.clientY - popupRect.height / 2;
    top = Math.max(8, Math.min(top, window.innerHeight - popupRect.height - 8));

    this.renderer.setStyle(this.popupEl, 'left', `${left}px`);
    this.renderer.setStyle(this.popupEl, 'top', `${top}px`);
  }

  private removePopup() {
    if (!this.popupEl) return;

    // remove listeners attached to popup
    if (this.popupMouseEnterHandler) {
      this.popupEl.removeEventListener('mouseenter', this.popupMouseEnterHandler);
      this.popupMouseEnterHandler = null;
    }
    if (this.popupMouseLeaveHandler) {
      this.popupEl.removeEventListener('mouseleave', this.popupMouseLeaveHandler);
      this.popupMouseLeaveHandler = null;
    }
    if (this.popupClickHandler) {
      this.popupEl.removeEventListener('click', this.popupClickHandler);
      this.popupClickHandler = null;
    }

    // remove document click listener
    if (this.documentClickHandler) {
      this.document.removeEventListener('click', this.documentClickHandler);
      this.documentClickHandler = null;
    }

    // remove Esc key handler
    if (this.documentKeydownHandler) {
      this.document.removeEventListener('keydown', this.documentKeydownHandler);
      this.documentKeydownHandler = null;
    }

    this.popupEl.remove();
    this.popupEl = null;

    if (this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }

  /** Inject spinner CSS once */
  private ensureSpinnerStyle() {
    if (ImageHoverPopupDirective.spinnerStyleInjected) return;
    ImageHoverPopupDirective.spinnerStyleInjected = true;
    const css = `
    @keyframes ihp-spin { to { transform: rotate(360deg); } }
    .image-hover-loader .ihp-spinner {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      border: 3px solid rgba(0,0,0,0.12);
      border-top-color: rgba(0,0,0,0.6);
      box-sizing: border-box;
      animation: ihp-spin 1s linear infinite;
    }
    .image-hover-loader {
      background: rgba(255,255,255,0.95);
      border-radius: 8px;
      box-shadow: 0 8px 24px rgba(0,0,0,0.18);
      backdrop-filter: blur(4px);
    }`;
    const style = this.renderer.createElement('style');
    this.renderer.setProperty(style, 'textContent', css);
    this.document.head.appendChild(style);
  }
}
